<template>
    <div class="pm">

        <!-- ════════════════════ DASHBOARD HEADER ════════════════════ -->
        <div class="pm-dash-header">
            <div class="pm-dash-header-top">
                <div>
                    <div class="pm-eyebrow">TALLER · ALMACÉN PROGRESS</div>
                    <h1 class="pm-title">Progreso de almacén</h1>
                </div>
                <div class="pm-header-actions">
                    <div class="pm-stat-pill">
                        <i class="fa-solid fa-list-check"></i>
                        <strong>{{ listadoPedidos.length }}</strong>
                        <span>pedidos</span>
                    </div>
                    <div class="pm-stat-pill pm-stat-pill--cta">
                        <i class="fa-solid fa-hand"></i>
                        <strong>{{ totalManoPendiente }}</strong>
                        <span>uds por almacenar</span>
                    </div>
                    <div class="pm-stat-pill pm-stat-pill--success" v-if="pedidosListos > 0">
                        <i class="fa-solid fa-check-double"></i>
                        <strong>{{ pedidosListos }}</strong>
                        <span>listos</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ════ Buscador: en qué pedidos falta un producto ════ -->
        <div class="pm-buscador">
            <div class="pm-search-box">
                <i class="fa-solid fa-magnifying-glass pm-search-icon"></i>
                <b-form-input
                    v-model="busquedaProducto"
                    placeholder="Buscar producto por nombre o SKU…"
                    class="pm-search-input"
                />
                <i v-if="busquedaProducto" class="fa-solid fa-xmark pm-search-clear" @click="busquedaProducto = ''"></i>
            </div>

            <div v-if="busquedaProducto.trim()" class="pm-resultados">
                <div v-if="resultadosBusqueda.length === 0" class="pm-resultados-vacio">
                    Ningún pedido activo con ese producto.
                </div>
                <div
                    v-for="r in resultadosBusqueda"
                    :key="r.pedido.id"
                    class="pm-resultado-card"
                    @click="seleccionarPedido(r.pedido)"
                >
                    <div class="pm-resultado-head">
                        <strong>{{ r.pedido.codigo }}</strong>
                        <span v-if="r.faltaTotal > 0" class="pm-resultado-falta">{{ r.faltaTotal }} uds faltan</span>
                        <span v-else class="pm-resultado-completo">✓ completo</span>
                    </div>
                    <div class="pm-resultado-items">
                        <div v-for="it in r.items" :key="it.sku" class="pm-resultado-item">
                            <span class="pm-item-sku">{{ it.sku }}</span>
                            <span class="pm-item-badge" :class="{ 'pm-item-badge--ok': it.total - it.mano <= 0 }">
                                <template v-if="it.total - it.mano > 0">faltan <strong>{{ it.total - it.mano }}</strong></template>
                                <template v-else>completo</template>
                                <span class="pm-item-frac">({{ it.mano }}/{{ it.total }})</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <b-card-code id="card-progreso">

            <div v-if="cargandoPedidos" class="text-center text-muted py-4">
                <b-spinner small class="mr-1"></b-spinner>
                <span>Cargando pedidos...</span>
            </div>

            <template v-else>
            <!-- Tarjetas de Pedidos rediseñadas -->
            <div class="pedidos-cards mb-3">
                <div
                    v-for="pedido in listadoPedidos"
                    :key="pedido.id"
                    class="pedido-card"
                    :class="{
                        'pedido-card-selected': pedidoSeleccionado && pedidoSeleccionado.id === pedido.id,
                        'pedido-card-listo': getVariantEstadoMano(pedido) === 'success'
                    }"
                    @click="seleccionarPedido(pedido)"
                >
                    <div class="pedido-card-head">
                        <div class="pedido-card-codigo">{{ pedido.codigo }}</div>
                        <div class="pedido-card-head-right">
                            <span class="pedido-card-estado-pill" :class="'pms-' + getVariantEstadoMano(pedido)">
                                <i class="fa-solid fa-circle"></i>
                                {{ getEstadoMano(pedido) }}
                            </span>
                        </div>
                    </div>

                    <div class="pedido-card-meta">
                        <span><i class="fa-regular fa-calendar"></i> {{ pedido.fecha }}</span>
                        <span class="pedido-card-meta-sep">·</span>
                        <span class="pedido-card-age" :class="ageClass(pedido.fecha)">{{ formatFechaRelativa(pedido.fecha) }}</span>
                    </div>

                    <div class="pedido-card-stats">
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">REFS</div>
                            <div class="pedido-stat-value">{{ pedidoStats(pedido).refs }}</div>
                        </div>
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">UDS</div>
                            <div class="pedido-stat-value">{{ pedidoStats(pedido).total }}</div>
                        </div>
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">ALMACÉN</div>
                            <div class="pedido-stat-value pedido-stat-value--cta">{{ pedidoStats(pedido).mano }}</div>
                        </div>
                    </div>

                    <div class="pedido-card-progress">
                        <div class="pedido-progress-track">
                            <div class="pedido-progress-fill" :style="{ width: pedidoStats(pedido).pct + '%' }"></div>
                        </div>
                        <span class="pedido-progress-pct">{{ pedidoStats(pedido).pct }}%</span>
                    </div>

                    <div class="pedido-card-progress pedido-card-progress--albaran" v-b-tooltip.hover.top :title="pedidoStats(pedido).pendAlbaran + ' de ' + pedidoStats(pedido).completados + ' uds completadas sin enviar a albarán'">
                        <span class="pedido-progress-label"><i class="fa-solid fa-file-export"></i></span>
                        <div class="pedido-progress-track">
                            <div class="pedido-progress-fill" :style="{ width: pedidoStats(pedido).albaranP + '%' }"></div>
                        </div>
                        <span class="pedido-progress-pct">{{ pedidoStats(pedido).albaranP }}%</span>
                    </div>
                </div>
            </div>

            <!-- Tabla de Items por Color -->
            <b-table
                v-if="itemsAgrupados.length > 0"
                :items="itemsAgrupados"
                :fields="columnasTable"
                class="table-borderless table-progreso table-ultra-compact"
                small
                striped
            >
                <template #cell(sku)="data">
                    <strong>{{data.item.sku }}</strong>
                </template>

                <template v-for="columna in columnasTable.filter((f) => f.key.startsWith('mano_'))"
                    #[`cell(${columna.key})`]="data">
                    <div :key="columna.key" class="cell-progreso" :class="data.item[columna.key.replace('mano_', 'terminado_')] ? 'is-terminado' : 'is-pendiente'" v-if="data.item[columna.key.replace('mano_', 'id_')]">
                        <div class="progreso-info">
                            <span class="progreso-actual">{{ data.item[columna.key] || 0 }}</span>
                            <span class="progreso-total">/ {{ data.item[columna.key.replace('mano_', 'total_')] }}</span>
                            <i
                                v-if="data.item[columna.key] > 0"
                                class="fa-solid fa-pen btn-editar-mano"
                                title="Corregir cantidad enviada"
                                @click="editarMano(data.item, columna.key)"
                            ></i>
                        </div>
                        <b-progress
                            :value="data.item[columna.key] || 0"
                            :max="data.item[columna.key.replace('mano_', 'total_')]"
                            height="4px"
                            class="mb-1"
                            variant="warning"
                        ></b-progress>
                        <div class="input-progreso" v-if="data.item[columna.key] < data.item[columna.key.replace('mano_', 'total_')]">
                            <b-form-input
                                v-model.number="data.item[columna.key.replace('mano_', 'input_')]"
                                type="number"
                                size="sm"
                                class="input-cantidad"
                                min="1"
                                placeholder="+N"
                                @wheel.prevent
                                @keyup.enter="incrementarProgreso(data.item, columna.key)"
                            />
                            <span
                                class="btn-incrementar"
                                @click="incrementarProgreso(data.item, columna.key)"
                            >
                                <i class="fa-solid fa-plus"></i>
                            </span>
                        </div>
                        <div v-else class="text-success text-center">
                            <i class="fa-solid fa-check"></i>
                        </div>
                    </div>
                </template>
            </b-table>

            <div v-else-if="pedidoSeleccionado" class="text-center text-muted py-3">
                No hay items en este pedido
            </div>

            <div v-else class="text-center text-muted py-3">
                Selecciona un pedido para ver su progreso
            </div>
            </template>

        </b-card-code>
    </div>
</template>

<script>
import axios from '@axios'
import { BTable, BFormInput, BFormGroup, BBadge, BProgress, VBTooltip } from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code'
import pedirCantidadCorregida from '@/utils/corregirCantidad'

export default {
    components: { BCardCode, BTable, BFormInput, BFormGroup, BBadge, BProgress },
    directives: { 'b-tooltip': VBTooltip },
    data() {
        return {
            columnasTable: [
                { key: "sku", label: "Producto", stickyColumn: true, thStyle: { width: "150px" } },
            ],
            listadoPedidos: [],
            cargandoPedidos: true,
            busquedaProducto: '',
            pedidoSeleccionado: null,
            itemsPedido: [],
            itemsAgrupados: [],
            skuTitulos: {},
            coloresDisponibles: [],
        }
    },
    computed: {
        totalManoPendiente() {
            var total = 0;
            (this.listadoPedidos || []).forEach(p => {
                if (!p.listaItems) return;
                p.listaItems.forEach(i => {
                    var falta = (Number(i.cantidadTotal) || 0) - (Number(i.cantidadMano) || 0);
                    if (falta > 0) total += falta;
                });
            });
            return total;
        },
        pedidosListos() {
            return (this.listadoPedidos || []).filter(p => this.getVariantEstadoMano(p) === 'success').length;
        },
        // Pedidos con el producto buscado; primero los que aún tienen uds por almacenar
        resultadosBusqueda() {
            const q = this.busquedaProducto.trim().toLowerCase();
            if (!q) return [];
            const res = [];
            (this.listadoPedidos || []).forEach(pedido => {
                const items = (pedido.listaItems || [])
                    .map(i => ({
                        sku: i.sku,
                        nombre: this.getNombreProducto(i.sku),
                        total: Number(i.cantidadTotal) || 0,
                        mano: Number(i.cantidadMano) || 0,
                    }))
                    .filter(i => (i.sku || '').toLowerCase().includes(q) || i.nombre.toLowerCase().includes(q));
                if (items.length) {
                    const faltaTotal = items.reduce((s, i) => s + Math.max(i.total - i.mano, 0), 0);
                    res.push({ pedido, items, faltaTotal });
                }
            });
            return res.sort((a, b) => (b.faltaTotal > 0) - (a.faltaTotal > 0));
        },
    },
    methods: {
        getNombreProducto(sku) {
            const lastDash = (sku || '').lastIndexOf('-');
            const raiz = lastDash > 0 ? sku.substring(0, lastDash) : sku;
            return this.skuTitulos[raiz] || raiz;
        },
        // Uds listas para albarán: lo que hay en almacén; si está marcado como
        // terminado y almacén está a 0, se usa lo cortado.
        listoAlbaran(item) {
            const mano = Number(item.cantidadMano) || 0;
            if (item.terminado && mano === 0) return Number(item.cantidadCorte) || 0;
            return mano;
        },
        pedidoStats(pedido) {
            if (!pedido || !pedido.listaItems || pedido.listaItems.length === 0) {
                return { refs: 0, total: 0, mano: 0, pct: 0, completados: 0, pendAlbaran: 0, albaranP: 0 };
            }
            const items = pedido.listaItems;
            const total = items.reduce((s, i) => s + (Number(i.cantidadTotal) || 0), 0);
            const mano  = items.reduce((s, i) => s + (Number(i.cantidadMano)  || 0), 0);
            // Pendiente de albarán: listo (ver listoAlbaran) que no se ha enviado
            const baseDe = i => this.listoAlbaran(i);
            const completados = items.reduce((s, i) => s + baseDe(i), 0);
            const pendAlbaran = items.reduce((s, i) => s + Math.max(baseDe(i) - (Number(i.cantidadAlbaran) || 0), 0), 0);
            // Enviado a albarán (sin pasar del total de cada SKU)
            const enviado = items.reduce((s, i) => s + Math.min(Number(i.cantidadAlbaran) || 0, Number(i.cantidadTotal) || 0), 0);
            return {
                completados: completados,
                pendAlbaran: pendAlbaran,
                albaranP: total > 0 ? Math.round((enviado / total) * 100) : 0,
                refs: items.length,
                total: total,
                mano: mano,
                pct: total > 0 ? Math.round((mano / total) * 100) : 0,
            };
        },
        formatFechaRelativa(fecha) {
            if (!fecha) return '—';
            const d = new Date(fecha);
            if (isNaN(d.getTime())) return fecha;
            const diffDias = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
            if (diffDias === 0) return 'hoy';
            if (diffDias === 1) return 'ayer';
            if (diffDias < 7)   return 'hace ' + diffDias + ' días';
            if (diffDias < 30)  return 'hace ' + Math.floor(diffDias / 7) + ' sem';
            if (diffDias < 365) return 'hace ' + Math.floor(diffDias / 30) + ' meses';
            return 'hace ' + Math.floor(diffDias / 365) + ' años';
        },
        ageClass(fecha) {
            if (!fecha) return '';
            const d = new Date(fecha);
            if (isNaN(d.getTime())) return '';
            const dias = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
            if (dias < 7) return 'age-fresh';
            if (dias < 15) return 'age-warn';
            return 'age-stale';
        },
        async loadPedidos() {
            try {
                axios.post("/uso", { seccion: "progresoMano" });

                const resTitulos = await axios.get("/sku/title");
                resTitulos.data.forEach(item => {
                    this.skuTitulos[item.skuSingle] = item.productTitle;
                });

                const response = await axios.get("/taller/pedido/vero");
                var pedidos = response.data.filter(function(p) { return p.estado !== 'Terminado'; });

                // Cargar items de cada pedido (en paralelo) para calcular estado de mano
                await Promise.all(pedidos.map(async pedido => {
                    try {
                        const resItems = await axios.get('/taller/pedido/vero/items/' + pedido.id);
                        pedido.listaItems = resItems.data;
                    } catch (e) {
                        pedido.listaItems = [];
                    }
                }));

                this.listadoPedidos = pedidos;
            } catch (error) {
                console.error("Error cargando pedidos:", error);
            } finally {
                this.cargandoPedidos = false;
            }
        },
        seleccionarPedido(pedido) {
            this.pedidoSeleccionado = pedido;
            this.cargarItemsPedido();
        },
        async cargarItemsPedido() {
            if (!this.pedidoSeleccionado) {
                this.itemsPedido = [];
                this.itemsAgrupados = [];
                return;
            }
            try {
                const response = await axios.get(`/taller/pedido/vero/faltantes/${this.pedidoSeleccionado.id}`);
                this.itemsPedido = response.data;
                this.agruparPorRaiz();
            } catch (error) {
                console.error("Error cargando items:", error);
            }
        },
        agruparPorRaiz() {
            const grupos = {};
            const coloresSet = new Set();

            this.itemsPedido.forEach(item => {
                const lastDash = item.sku.lastIndexOf('-');
                const raiz = item.sku.substring(0, lastDash);
                const color = item.sku.substring(lastDash + 1).toLowerCase();

                coloresSet.add(color);

                if (!grupos[raiz]) {
                    grupos[raiz] = { sku: raiz };
                }

                grupos[raiz][`id_${color}`] = item.id;
                grupos[raiz][`total_${color}`] = item.cantidadTotal;
                grupos[raiz][`mano_${color}`] = item.cantidadMano;
                grupos[raiz][`terminado_${color}`] = item.terminado;
                grupos[raiz][`input_${color}`] = null;
            });

            // Actualizar columnas con orden específico
            const ordenColores = ['tan', 'black', 'blk', 'db', 'navy', 'grey', 'mocha', 'olive', 'camel', 'caoba', 'brown', 'army', 'amber', 'stone', 'hab', 'khaki', 'khk'];
            this.coloresDisponibles = Array.from(coloresSet).sort((a, b) => {
                const indexA = ordenColores.indexOf(a);
                const indexB = ordenColores.indexOf(b);
                if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            this.columnasTable = [
                { key: "sku", label: "Producto", stickyColumn: true, thStyle: { width: "150px" } },
            ];

            this.coloresDisponibles.forEach(color => {
                this.columnasTable.push({
                    key: `mano_${color}`,
                    label: color.toUpperCase(),
                    thStyle: { width: "80px", textAlign: "center" }
                });
            });

            this.itemsAgrupados = Object.values(grupos);
        },
        getEstadoMano(pedido) {
            var items = pedido.listaItems;
            if (!items || items.length === 0) return pedido.estado;
            var todoMano = items.every(function(i) { return i.cantidadMano >= i.cantidadTotal; });
            if (todoMano) return 'Almacén listo';
            return pedido.estado;
        },
        getVariantEstadoMano(pedido) {
            var items = pedido.listaItems;
            if (!items || items.length === 0) return 'light';
            var todoMano = items.every(function(i) { return i.cantidadMano >= i.cantidadTotal; });
            if (todoMano) return 'success';
            if (pedido.estado === 'En proceso') return 'warning';
            return 'light';
        },
        async incrementarProgreso(item, columnaKey) {
            const color = columnaKey.replace('mano_', '');
            const inputKey = `input_${color}`;
            const idKey = `id_${color}`;
            const cantidadNueva = item[inputKey];

            if (!cantidadNueva || cantidadNueva < 1) return;

            try {
                const response = await axios.put("/taller/pedido/vero/progreso", {
                    id: item[idKey],
                    columna: 'cantidadMano',
                    cantidadNueva: cantidadNueva
                });

                this.$swal({
                    icon: 'success',
                    title: 'Guardado',
                    text: `+${cantidadNueva} registrado`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                });

                item[inputKey] = null;
                await this.refrescarPedidoSeleccionado();
            } catch (error) {
                console.error("Error actualizando progreso:", error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo actualizar el progreso',
                });
            }
        },
        async refrescarPedidoSeleccionado() {
            await this.cargarItemsPedido();

            // Actualizar listaItems del pedido seleccionado para refrescar badge
            if (this.pedidoSeleccionado) {
                try {
                    var resItems = await axios.get('/taller/pedido/vero/items/' + this.pedidoSeleccionado.id);
                    this.pedidoSeleccionado.listaItems = resItems.data;
                } catch (e) {}
            }
        },
        // Corrige el total enviado a almacén; el backend ajusta también el albarán con la diferencia
        async editarMano(item, columnaKey) {
            const color = columnaKey.replace('mano_', '');
            const actual = item[columnaKey] || 0;

            const value = await pedirCantidadCorregida(this.$swal, {
                sku: `${item.sku}-${color.toUpperCase()}`,
                label: 'Total enviado a almacén',
                actual,
                total: item[`total_${color}`] || 0,
                detalleDiff: 'en la última línea del albarán',
            });
            if (value === null) return;

            try {
                await axios.put('/taller/pedido/vero/item/corregir', {
                    id: item[`id_${color}`],
                    columna: 'cantidadMano',
                    cantidad: value,
                });

                this.$swal({
                    icon: 'success',
                    title: 'Corregido',
                    text: `${actual} → ${value}`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                });

                await this.refrescarPedidoSeleccionado();
            } catch (error) {
                console.error("Error corrigiendo cantidad:", error);
                this.$swal({ icon: 'error', title: 'Error', text: 'No se pudo corregir la cantidad' });
            }
        },
    },
    mounted() {
        this.loadPedidos();
    }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// Tokens
$pm-bg:        #F8FAFC;
$pm-surface:   #FFFFFF;
$pm-surface-2: #F1F5F9;
$pm-border:    #E2E8F0;
$pm-divider:   #EEF2F7;
$pm-ink:       #1E293B;
$pm-mute:      #64748B;
$pm-soft:      #94A3B8;
$pm-primary:   #3B82F6;
$pm-cta:       #F97316;
$pm-emerald:   #10B981;
$pm-coral:     #EF4444;
$pm-amber:     #F59E0B;

.pm {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: $pm-bg;
    color: $pm-ink;
    margin: -2rem;
    padding: 28px 32px 56px;
    min-height: calc(100vh - 4rem);
    font-feature-settings: "tnum", "zero";
}

// ──────────── BUSCADOR DE PRODUCTO ────────────
.pm-buscador {
    margin-bottom: 20px;
}

.pm-search-box {
    position: relative;
    max-width: 480px;

    .pm-search-icon {
        position: absolute;
        left: 14px; top: 50%;
        transform: translateY(-50%);
        color: $pm-soft;
        pointer-events: none;
    }
    .pm-search-input {
        padding-left: 38px;
        padding-right: 34px;
        height: 44px;
        border-radius: 12px;
        border: 1px solid $pm-border;
        background: $pm-surface;
        font-weight: 600;
        &:focus { border-color: $pm-primary; box-shadow: none; }
    }
    .pm-search-clear {
        position: absolute;
        right: 14px; top: 50%;
        transform: translateY(-50%);
        color: $pm-soft;
        cursor: pointer;
        &:hover { color: $pm-coral; }
    }
}

.pm-resultados {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 12px;
    align-items: start;
}
.pm-resultados-vacio {
    grid-column: 1 / -1;
    color: $pm-mute;
    font-size: 0.85rem;
    padding: 8px 2px;
}
.pm-resultado-card {
    background: $pm-surface;
    border: 1px solid $pm-border;
    border-radius: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:hover {
        border-color: $pm-primary;
        box-shadow: 0 2px 10px rgba(59, 130, 246, 0.12);
    }
}
.pm-resultado-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    strong { color: $pm-ink; font-size: 0.95rem; }
    .pm-resultado-falta {
        color: $pm-cta;
        font-weight: 700;
        font-size: 0.8rem;
    }
    .pm-resultado-completo {
        color: #16a34a;
        font-weight: 700;
        font-size: 0.8rem;
    }
}
.pm-resultado-items {
    display: flex;
    flex-direction: column;
}
.pm-resultado-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    font-size: 0.82rem;
    border-top: 1px solid $pm-divider;

    .pm-item-sku {
        font-weight: 600;
        color: $pm-ink;
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: -0.2px;
    }
    .pm-item-badge {
        flex: 0 0 auto;
        color: $pm-cta;
        font-weight: 600;
        white-space: nowrap;
        strong { font-weight: 800; }
        .pm-item-frac { color: $pm-soft; font-weight: 500; margin-left: 2px; }
        &.pm-item-badge--ok { color: #16a34a; }
    }
}

// ──────────── DASHBOARD HEADER ────────────
.pm-dash-header {
    background: $pm-surface;
    border: 1px solid $pm-border;
    border-radius: 14px;
    padding: 18px 20px 16px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.pm-dash-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.pm-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $pm-primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
        content: '';
        display: inline-block;
        width: 6px;
        height: 6px;
        background: $pm-cta;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
        animation: pm-pulse 2s infinite;
    }
}

.pm-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: $pm-ink;
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0;
}

.pm-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.pm-stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    background: $pm-bg;
    border: 1px solid $pm-border;
    border-radius: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: $pm-mute;

    i { color: $pm-primary; font-size: 12px; }
    strong {
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px;
        font-weight: 700;
        color: $pm-ink;
        font-feature-settings: "tnum";
    }

    &--cta {
        background: rgba(249, 115, 22, 0.06);
        border-color: rgba(249, 115, 22, 0.2);
        color: darken($pm-cta, 8%);
        i { color: $pm-cta; }
        strong { color: darken($pm-cta, 8%); }
    }
    &--success {
        background: rgba(16, 185, 129, 0.06);
        border-color: rgba(16, 185, 129, 0.2);
        color: darken($pm-emerald, 8%);
        i { color: $pm-emerald; }
        strong { color: darken($pm-emerald, 8%); }
    }
}

#card-progreso .card-header {
    display: none;
}

// ──────────── PEDIDOS GRID ────────────
.pedidos-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
}

.pedido-card {
    background: $pm-surface;
    border: 1px solid $pm-border;
    border-radius: 12px;
    padding: 14px 16px 12px;
    cursor: pointer;
    transition: all 200ms ease;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:hover {
        border-color: $pm-primary;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        transform: translateY(-1px);
    }

    &.pedido-card-listo { border-color: rgba(16, 185, 129, 0.4); }

    &.pedido-card-selected {
        border-color: $pm-primary;
        background: linear-gradient(180deg, rgba(59, 130, 246, 0.04) 0%, $pm-surface 100%);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    }
}

.pedido-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.pedido-card-head-right {
    display: flex;
    align-items: center;
    gap: 6px;
}

.pedido-card-codigo {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: $pm-ink;
    letter-spacing: 0.02em;
}

.pedido-card-estado-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 999px;

    i { font-size: 5px; }

    &.pms-success { background: rgba(16, 185, 129, 0.12); color: $pm-emerald; }
    &.pms-warning { background: rgba(245, 158, 11, 0.12); color: darken($pm-amber, 8%); }
    &.pms-light   { background: $pm-surface-2; color: $pm-mute; }
    &.pms-danger  { background: rgba(239, 68, 68, 0.12); color: $pm-coral; }
}

.pedido-card-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: $pm-mute;

    i { font-size: 9px; opacity: 0.7; }

    .pedido-card-meta-sep { opacity: 0.4; }
}

.pedido-card-age {
    font-weight: 700;
    text-transform: lowercase;
    letter-spacing: 0.02em;

    &.age-fresh { color: $pm-emerald; }
    &.age-warn  { color: $pm-amber; }
    &.age-stale { color: $pm-coral; }
}

.pedido-card-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 8px 0;
    border-top: 1px solid $pm-divider;
    border-bottom: 1px solid $pm-divider;
}

.pedido-stat {
    display: flex;
    flex-direction: column;
    gap: 1px;
    text-align: center;
}

.pedido-stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8.5px;
    font-weight: 600;
    color: $pm-soft;
    letter-spacing: 0.08em;
}

.pedido-stat-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 15px;
    font-weight: 700;
    color: $pm-ink;
    line-height: 1.1;
    font-feature-settings: "tnum";

    &--cta { color: $pm-cta; }
}

.pedido-card-progress {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pedido-progress-track {
    flex: 1;
    height: 6px;
    background: $pm-surface-2;
    border-radius: 999px;
    overflow: hidden;
}

.pedido-progress-fill {
    height: 100%;
    background: $pm-cta;
    border-radius: 999px;
    transition: width 300ms ease;
}

.pedido-progress-pct {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: $pm-cta;
    width: 38px;
    text-align: right;
    font-feature-settings: "tnum";
}

// Barra de pendiente de albarán
.pedido-card-progress--albaran {
    margin-top: 6px;
    .pedido-progress-fill { background: $pm-primary; }
    .pedido-progress-pct  { color: $pm-primary; }
}

.pedido-progress-label {
    font-size: 10px;
    color: $pm-primary;
}

@keyframes pm-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
}

@media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
    }
}

.table-progreso {
    font-size: 0.85rem;

    th {
        text-align: center;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 11px;
    }

    td {
        vertical-align: middle;
        text-align: center;
        padding: 8px 4px !important;
    }

    td:first-child {
        text-align: left;
        padding-left: 10px !important;
    }
}

.cell-progreso {
    min-width: 70px;
    padding: 4px 6px;
    border-radius: 6px;

    // Terminado en verde, pendiente en ámbar (igual que el desglose de /pedido-taller-vero)
    &.is-terminado { background: rgba(#10B981, 0.12); }
    &.is-pendiente { background: rgba(#F59E0B, 0.12); }
}

.progreso-info {
    font-size: 12px;
    margin-bottom: 2px;
}

.progreso-actual {
    font-weight: bold;
    color: #fd7e14;
}

.progreso-total {
    color: #888;
}

.btn-editar-mano {
    margin-left: 4px;
    font-size: 10px;
    color: #94A3B8;
    cursor: pointer;
    &:hover { color: #3B82F6; }
}

.input-progreso {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
}

.input-cantidad {
    width: 45px !important;
    height: 24px !important;
    font-size: 11px !important;
    padding: 2px 4px !important;
    text-align: center;
}

.btn-incrementar {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fd7e14;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 10px;

    &:hover {
        background: #e96b02;
    }
}

.table-ultra-compact td {
    min-width: 70px !important;
    max-width: 90px !important;
}

.table-ultra-compact td:first-child {
    min-width: 150px !important;
    max-width: 200px !important;
}
</style>

<style lang="scss">
// ══════════ DARK MODE ══════════ (sin scoped: .dark-layout está en body)
// Paleta negra, misma que el listado de pedidos Vero
.dark-layout .pm {
    background: #161616;
    color: #d0d2d6;

    .pm-dash-header { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
    .pm-title { color: #e4e6eb; }
    .pm-stat-pill { background: #161616; border-color: #2c2c2c; color: #8e9099; }
    .pm-stat-pill strong { color: #e4e6eb; }
    .pm-stat-pill--cta, .pm-stat-pill--cta strong { color: #F97316; }
    .pm-stat-pill--success, .pm-stat-pill--success strong { color: #10B981; }

    .pm-search-box .pm-search-input { background: #1f1f1f; border-color: #2c2c2c; color: #d0d2d6; }
    .pm-resultado-card { background: #1f1f1f; border-color: #2c2c2c; }
    .pm-resultado-head strong,
    .pm-resultado-item .pm-item-sku { color: #e4e6eb; }
    .pm-resultado-item { border-top-color: #2c2c2c; }

    .pedido-card { background: #161616; border-color: #2c2c2c; }
    .pedido-card:hover { border-color: #3B82F6; box-shadow: none; }
    .pedido-card.pedido-card-selected { background: rgba(59, 130, 246, 0.08); border-color: #3B82F6; }
    .pedido-card-codigo,
    .pedido-stat-value:not(.pedido-stat-value--cta) { color: #e4e6eb; }
    .pedido-card-meta { color: #8e9099; }
    .pedido-card-stats { border-color: #2c2c2c; }
    .pedido-card-estado-pill.pms-light { background: #2c2c2c; color: #b4b7bd; }
    .pedido-progress-track { background: #2c2c2c; }

    .table-progreso th { background: #161616 !important; color: #b4b7bd; }
    // Filas alternas y columna fija (Producto): bootstrap-vue le pone fondo claro
    .table-progreso tbody tr { background: #1f1f1f !important; }
    .table-progreso tbody tr:nth-child(even) { background: #242424 !important; }
    .table-progreso tbody .b-table-sticky-column { background: inherit !important; color: #e4e6eb; }
    .table-progreso .progress { background: #2c2c2c; }
}
</style>
